import { Checkbox, CheckboxGroup, Icon } from '@components/ui';
import { CheckboxOption } from '@types';
import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { SelectedFiltersType } from 'src/pages/dashboard';

import styles from './FilterTree.module.scss';

interface FilterTreeProps {
  items: { item: CheckboxOption; subitems: CheckboxOption[] }[];
  selectedFilters: SelectedFiltersType;
  onUseCaseSelect: (keys: string[]) => void;
  onModelSelect: (keys: string[]) => void;
}

export const FilterTree: React.FC<FilterTreeProps> = ({ items, selectedFilters, onUseCaseSelect, onModelSelect }) => {
  const [expandedItem, setExpandedItem] = useState<number>();
  const treeRef = useRef<HTMLDivElement | null>(null);

  function toggleUsecase(option: CheckboxOption, index: number) {
    if (selectedFilters.useCases.includes(option.id.toString())) {
      // unselect use case and related models
      onUseCaseSelect(selectedFilters.useCases.filter(selectedKey => selectedKey !== option.id));
      onModelSelect(
        selectedFilters.models?.filter(model => !items[index].subitems.some(subItem => subItem.name === model)),
      );
    } else {
      // select use case and related models
      onUseCaseSelect([...selectedFilters.useCases, option.id.toString()]);
      onModelSelect([
        ...selectedFilters.models,
        ...(items
          .find(item => item.item.id === option.id)
          ?.subitems.filter(model => !selectedFilters.models.includes(model.id.toString()))
          .map(model => model.name as string) || []),
      ]);
    }
    setExpandedItem(index);
  }

  function toggleModel(models: string[], index: number, selectedModelName: string) {
    onModelSelect(models);
    if (
      !selectedFilters.models.includes(selectedModelName) &&
      !selectedFilters.useCases.includes(items[index].item.id.toString())
    ) {
      // use case is not selected but model is => select related use case
      onUseCaseSelect([...selectedFilters.useCases, items[index].item.id.toString()]);
    }
  }

  return (
    <div
      ref={treeRef}
      className={styles.tree}
    >
      {items.map((item, index) => (
        <div
          key={item.item.id}
          className={styles.item}
        >
          <div className={styles.useCase}>
            <div
              className={clsx(styles.arrow, { [styles.active]: expandedItem === index })}
              onClick={() => {
                if (expandedItem === index) {
                  setExpandedItem(undefined);
                } else {
                  setExpandedItem(index);
                }
              }}
            >
              <Icon name="angle-right" />
            </div>
            <Checkbox
              toggleOption={option => toggleUsecase(option, index)}
              isChecked={selectedFilters.useCases.some(useCase => useCase === item.item.name)}
              option={{ id: item?.item?.name as string, name: item.item.name }}
            />
          </div>
          <div
            className={styles.checkboxGroupWrapper}
            style={{
              maxHeight:
                index === expandedItem
                  ? treeRef.current?.children[index]
                      .querySelector('[class*="FilterTree_checkboxGroup__"')
                      ?.getBoundingClientRect().height
                  : 0,
            }}
          >
            <CheckboxGroup
              className={styles.checkboxGroup}
              defaultSelected={selectedFilters.models}
              onSelect={(keys, optionId) => toggleModel(keys, index, optionId)}
              options={item?.subitems?.map((subitem: CheckboxOption) => ({ id: subitem.id, name: subitem.name }))}
              disabledSearchList
            />
          </div>
        </div>
      ))}
    </div>
  );
};
